import { useState, useEffect, useMemo, useRef } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  ChevronUpIcon, 
  ChevronDownIcon,
  TableCellsIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

// Aggregation functions available
const AGGREGATIONS = [
  { value: 'none', label: 'No Aggregation', description: 'Raw value' },
  { value: 'COUNT', label: 'Count', description: 'Number of rows' },
  { value: 'COUNT_DISTINCT', label: 'Count Distinct', description: 'Unique values' },
  { value: 'SUM', label: 'Sum', description: 'Total of values' },
  { value: 'AVG', label: 'Average', description: 'Mean value' },
  { value: 'MIN', label: 'Minimum', description: 'Smallest value' },
  { value: 'MAX', label: 'Maximum', description: 'Largest value' },
];

// Filter operators
const OPERATORS = [
  { value: '=', label: 'Equals', types: ['all'] },
  { value: '!=', label: 'Not Equals', types: ['all'] },
  { value: '>', label: 'Greater Than', types: ['number', 'date'] },
  { value: '<', label: 'Less Than', types: ['number', 'date'] },
  { value: '>=', label: 'Greater or Equal', types: ['number', 'date'] },
  { value: '<=', label: 'Less or Equal', types: ['number', 'date'] },
  { value: 'LIKE', label: 'Contains', types: ['string'] },
  { value: 'NOT LIKE', label: 'Does Not Contain', types: ['string'] },
  { value: 'IS NULL', label: 'Is Empty', types: ['all'] },
  { value: 'IS NOT NULL', label: 'Is Not Empty', types: ['all'] },
  { value: 'IN', label: 'Is One Of', types: ['all'] },
];

// Sort directions
const SORT_DIRECTIONS = [
  { value: 'ASC', label: '↑ Ascending' },
  { value: 'DESC', label: '↓ Descending' },
];

/**
 * Visual Chart Builder Component
 * Allows users to build queries visually without writing SQL
 */
export default function VisualChartBuilder({ 
  schema, 
  chartType,
  sourceType,
  tableName,
  onQueryGenerated,
  initialConfig = null 
}) {
  // Selected columns state
  const [dimensions, setDimensions] = useState(initialConfig?.dimensions || []);
  const [measures, setMeasures] = useState(initialConfig?.measures || []);
  const [filters, setFilters] = useState(initialConfig?.filters || []);
  const [sortBy, setSortBy] = useState(initialConfig?.sortBy || []);
  const [limit, setLimit] = useState(initialConfig?.limit || 100);
  
  // Track if we should skip the initial SQL generation (to preserve existing complex SQL)
  // Also check for isComplexQuery flag which indicates a query that can't be represented in visual builder
  const isComplexQuery = initialConfig?.isComplexQuery === true;
  const skipInitialGeneration = useRef(!!initialConfig);
  const hasUserModified = useRef(false);
  
  // Get columns from schema
  const columns = useMemo(() => {
    if (!schema?.tables?.[0]?.columns) return [];
    return schema.tables[0].columns.map(col => ({
      name: col.name,
      type: inferColumnType(col.type || col.name),
      original: col
    }));
  }, [schema]);
  
  // Infer column type from name or type string
  function inferColumnType(typeOrName) {
    const name = typeOrName.toLowerCase();
    if (name.includes('count') || name.includes('amount') || name.includes('total') || 
        name.includes('number') || name.includes('integer') || name.includes('float') ||
        name.includes('decimal') || name.includes('numeric')) {
      return 'number';
    }
    if (name.includes('date') || name.includes('time') || name.includes('created') || 
        name.includes('updated') || name.includes('timestamp')) {
      return 'date';
    }
    if (name.includes('bool') || name.includes('is_') || name.includes('has_')) {
      return 'boolean';
    }
    return 'string';
  }
  
  // Generate SQL from visual config
  useEffect(() => {
    // Skip the initial generation if we loaded from an existing config
    // This preserves complex SQL that can't be fully represented in the visual builder
    if (skipInitialGeneration.current && !hasUserModified.current) {
      console.log('[VisualChartBuilder] Skipping initial SQL generation to preserve existing query');
      skipInitialGeneration.current = false; // Only skip once
      return;
    }
    
    // For complex queries (subqueries, CTEs, etc.), don't generate until user modifies
    if (isComplexQuery && !hasUserModified.current) {
      console.log('[VisualChartBuilder] Complex query detected, skipping SQL generation until user modifies');
      return;
    }
    
    const sql = generateSQL();
    onQueryGenerated(sql, { dimensions, measures, filters, sortBy, limit });
  }, [dimensions, measures, filters, sortBy, limit, tableName, isComplexQuery]);
  
  // Build the SELECT clause
  function buildSelectClause() {
    const selectParts = [];
    
    // Add dimensions (GROUP BY columns)
    dimensions.forEach(dim => {
      selectParts.push(dim.column);
    });
    
    // Add measures (aggregated columns)
    measures.forEach(measure => {
      if (measure.aggregation === 'none') {
        selectParts.push(measure.column);
      } else if (measure.aggregation === 'COUNT_DISTINCT') {
        selectParts.push(`COUNT(DISTINCT ${measure.column}) AS ${measure.alias || measure.column + '_count_distinct'}`);
      } else {
        selectParts.push(`${measure.aggregation}(${measure.column}) AS ${measure.alias || measure.column + '_' + measure.aggregation.toLowerCase()}`);
      }
    });
    
    // If nothing selected, select all
    if (selectParts.length === 0) {
      return '*';
    }
    
    return selectParts.join(',\n    ');
  }
  
  // Build the WHERE clause
  function buildWhereClause() {
    if (filters.length === 0) return '';
    
    const conditions = filters.map(filter => {
      if (filter.operator === 'IS NULL' || filter.operator === 'IS NOT NULL') {
        return `${filter.column} ${filter.operator}`;
      }
      if (filter.operator === 'LIKE' || filter.operator === 'NOT LIKE') {
        return `${filter.column} ${filter.operator} '%${filter.value}%'`;
      }
      if (filter.operator === 'IN') {
        const values = filter.value.split(',').map(v => `'${v.trim()}'`).join(', ');
        return `${filter.column} IN (${values})`;
      }
      // For string values, wrap in quotes
      const value = typeof filter.value === 'string' && 
                    !filter.value.match(/^-?\d+\.?\d*$/) 
                    ? `'${filter.value}'` 
                    : filter.value;
      return `${filter.column} ${filter.operator} ${value}`;
    });
    
    return `WHERE ${conditions.join('\n    AND ')}`;
  }
  
  // Build the GROUP BY clause
  function buildGroupByClause() {
    if (dimensions.length === 0 || measures.every(m => m.aggregation === 'none')) {
      return '';
    }
    return `GROUP BY ${dimensions.map((_, i) => i + 1).join(', ')}`;
  }
  
  // Build the ORDER BY clause
  function buildOrderByClause() {
    if (sortBy.length === 0) return '';
    return `ORDER BY ${sortBy.map(s => `${s.column} ${s.direction}`).join(', ')}`;
  }
  
  // Generate the full SQL query
  function generateSQL() {
    // Determine the dataset placeholder based on source type
    let datasetPlaceholder = '${dataset}';
    if (sourceType === 'mart') {
      datasetPlaceholder = '${dataset}_marts';
    } else if (sourceType === 'wideview') {
      datasetPlaceholder = '${dataset}_wide_views';
    }
    
    const select = buildSelectClause();
    const where = buildWhereClause();
    const groupBy = buildGroupByClause();
    const orderBy = buildOrderByClause();
    
    let sql = `SELECT\n    ${select}\nFROM ${datasetPlaceholder}.${tableName}`;
    if (where) sql += `\n${where}`;
    if (groupBy) sql += `\n${groupBy}`;
    if (orderBy) sql += `\n${orderBy}`;
    if (limit > 0) sql += `\nLIMIT ${limit}`;
    
    return sql;
  }
  
  // Mark that user has modified the config
  const markAsModified = () => {
    hasUserModified.current = true;
  };
  
  // Add a new dimension
  const addDimension = (column) => {
    if (!dimensions.find(d => d.column === column)) {
      markAsModified();
      setDimensions([...dimensions, { column }]);
    }
  };
  
  // Remove a dimension
  const removeDimension = (index) => {
    markAsModified();
    setDimensions(dimensions.filter((_, i) => i !== index));
  };
  
  // Add a new measure
  const addMeasure = (column) => {
    markAsModified();
    const newMeasure = {
      column,
      aggregation: 'COUNT',
      alias: ''
    };
    setMeasures([...measures, newMeasure]);
  };
  
  // Update a measure
  const updateMeasure = (index, updates) => {
    markAsModified();
    const newMeasures = [...measures];
    newMeasures[index] = { ...newMeasures[index], ...updates };
    setMeasures(newMeasures);
  };
  
  // Remove a measure
  const removeMeasure = (index) => {
    markAsModified();
    setMeasures(measures.filter((_, i) => i !== index));
  };
  
  // Add a new filter
  const addFilter = () => {
    markAsModified();
    setFilters([...filters, { 
      column: columns[0]?.name || '', 
      operator: '=', 
      value: '' 
    }]);
  };
  
  // Update a filter
  const updateFilter = (index, updates) => {
    markAsModified();
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], ...updates };
    setFilters(newFilters);
  };
  
  // Remove a filter
  const removeFilter = (index) => {
    markAsModified();
    setFilters(filters.filter((_, i) => i !== index));
  };
  
  // Add a sort
  const addSort = (column) => {
    if (!sortBy.find(s => s.column === column)) {
      markAsModified();
      setSortBy([...sortBy, { column, direction: 'DESC' }]);
    }
  };
  
  // Remove a sort
  const removeSort = (index) => {
    markAsModified();
    setSortBy(sortBy.filter((_, i) => i !== index));
  };
  
  // Update sort direction
  const toggleSortDirection = (index) => {
    markAsModified();
    const newSortBy = [...sortBy];
    newSortBy[index].direction = newSortBy[index].direction === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(newSortBy);
  };
  
  if (!schema || columns.length === 0) {
    return (
      <div className="p-6 text-center text-surface-500">
        <TableCellsIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Select a data source to start building your chart visually.</p>
      </div>
    );
  }
  
  // Check if we only have placeholder columns
  const hasRealColumns = columns.some(c => c.name !== '*');
  
  if (!hasRealColumns) {
    return (
      <div className="p-6 text-center">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
          <TableCellsIcon className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
            Schema Not Available
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
            The column information for this data source isn't available yet. 
            This may happen if the dbt model hasn't been deployed or if the table doesn't exist in BigQuery.
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-400">
            💡 <strong>Tip:</strong> Switch to SQL Editor mode to write your query directly, 
            or make sure the data source has been deployed first.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Warning for complex queries */}
      {isComplexQuery && !hasUserModified.current && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-amber-500 text-lg">⚠️</span>
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                Complex SQL Query Detected
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Your query contains advanced features (subqueries, CTEs, etc.) that cannot be fully represented in the Visual Builder. 
                Your <strong>original SQL will be preserved</strong> until you make changes here. 
                Any modifications will generate a simplified query.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Columns Panel */}
      <div className="grid grid-cols-2 gap-4">
        {/* Available Columns */}
        <div className="bg-surface-50 dark:bg-surface-800 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-surface-700 dark:text-surface-300 mb-3 flex items-center gap-2">
            <TableCellsIcon className="w-4 h-4" />
            Available Fields
          </h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {columns.map(col => (
              <div 
                key={col.name}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 group"
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    col.type === 'number' ? 'bg-blue-500' :
                    col.type === 'date' ? 'bg-purple-500' :
                    col.type === 'boolean' ? 'bg-orange-500' :
                    'bg-green-500'
                  }`} />
                  <span className="text-sm text-surface-700 dark:text-surface-300 font-mono">
                    {col.name}
                  </span>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => addDimension(col.name)}
                    className="px-2 py-1 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded hover:bg-emerald-200"
                    title="Add as Group By"
                  >
                    Group
                  </button>
                  <button
                    onClick={() => addMeasure(col.name)}
                    className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200"
                    title="Add as Measure"
                  >
                    Measure
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-surface-500 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500" /> Text
            <span className="w-2 h-2 rounded-full bg-blue-500" /> Number
            <span className="w-2 h-2 rounded-full bg-purple-500" /> Date
          </div>
        </div>
        
        {/* Selected Configuration */}
        <div className="space-y-4">
          {/* Dimensions (Group By) */}
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
            <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-2">
              📊 Group By (Dimensions)
            </h4>
            {dimensions.length === 0 ? (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 italic">
                Click "Group" on a field to add it here
              </p>
            ) : (
              <div className="space-y-2">
                {dimensions.map((dim, index) => (
                  <div key={index} className="flex items-center justify-between bg-white dark:bg-surface-800 p-2 rounded-lg">
                    <span className="text-sm font-mono text-surface-700 dark:text-surface-300">
                      {dim.column}
                    </span>
                    <button 
                      onClick={() => removeDimension(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Measures */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">
              📈 Measures (Aggregations)
            </h4>
            {measures.length === 0 ? (
              <p className="text-xs text-blue-600 dark:text-blue-400 italic">
                Click "Measure" on a field to add it here
              </p>
            ) : (
              <div className="space-y-2">
                {measures.map((measure, index) => (
                  <div key={index} className="bg-white dark:bg-surface-800 p-2 rounded-lg">
                    <div className="flex items-center gap-2">
                      <select
                        value={measure.aggregation}
                        onChange={(e) => updateMeasure(index, { aggregation: e.target.value })}
                        className="text-xs border border-surface-300 dark:border-surface-600 rounded px-2 py-1 bg-white dark:bg-surface-700"
                      >
                        {AGGREGATIONS.map(agg => (
                          <option key={agg.value} value={agg.value}>{agg.label}</option>
                        ))}
                      </select>
                      <span className="text-sm font-mono text-surface-700 dark:text-surface-300 flex-1">
                        ({measure.column})
                      </span>
                      <button 
                        onClick={() => removeMeasure(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="Alias (optional)"
                      value={measure.alias}
                      onChange={(e) => updateMeasure(index, { alias: e.target.value })}
                      className="mt-1 w-full text-xs border border-surface-300 dark:border-surface-600 rounded px-2 py-1 bg-white dark:bg-surface-700"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Filters Section */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300">
            🔍 Filters
          </h4>
          <button
            onClick={addFilter}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded hover:bg-amber-300"
          >
            <PlusIcon className="w-3 h-3" /> Add Filter
          </button>
        </div>
        
        {filters.length === 0 ? (
          <p className="text-xs text-amber-600 dark:text-amber-400 italic">
            No filters applied - showing all data
          </p>
        ) : (
          <div className="space-y-2">
            {filters.map((filter, index) => (
              <div key={index} className="flex items-center gap-2 bg-white dark:bg-surface-800 p-2 rounded-lg">
                <select
                  value={filter.column}
                  onChange={(e) => updateFilter(index, { column: e.target.value })}
                  className="text-xs border border-surface-300 dark:border-surface-600 rounded px-2 py-1 bg-white dark:bg-surface-700 flex-1"
                >
                  {columns.map(col => (
                    <option key={col.name} value={col.name}>{col.name}</option>
                  ))}
                </select>
                <select
                  value={filter.operator}
                  onChange={(e) => updateFilter(index, { operator: e.target.value })}
                  className="text-xs border border-surface-300 dark:border-surface-600 rounded px-2 py-1 bg-white dark:bg-surface-700"
                >
                  {OPERATORS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                {!['IS NULL', 'IS NOT NULL'].includes(filter.operator) && (
                  <input
                    type="text"
                    value={filter.value}
                    onChange={(e) => updateFilter(index, { value: e.target.value })}
                    placeholder="Value"
                    className="text-xs border border-surface-300 dark:border-surface-600 rounded px-2 py-1 bg-white dark:bg-surface-700 flex-1"
                  />
                )}
                <button 
                  onClick={() => removeFilter(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Sort and Limit Section */}
      <div className="grid grid-cols-2 gap-4">
        {/* Sort By */}
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300">
              ↕️ Sort By
            </h4>
          </div>
          
          {sortBy.length === 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-purple-600 dark:text-purple-400 italic">
                No sorting applied
              </p>
              <select
                onChange={(e) => e.target.value && addSort(e.target.value)}
                value=""
                className="w-full text-xs border border-purple-300 dark:border-purple-600 rounded px-2 py-1 bg-white dark:bg-surface-700"
              >
                <option value="">Add sort column...</option>
                {columns.map(col => (
                  <option key={col.name} value={col.name}>{col.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="space-y-2">
              {sortBy.map((sort, index) => (
                <div key={index} className="flex items-center gap-2 bg-white dark:bg-surface-800 p-2 rounded-lg">
                  <span className="text-sm font-mono text-surface-700 dark:text-surface-300 flex-1">
                    {sort.column}
                  </span>
                  <button
                    onClick={() => toggleSortDirection(index)}
                    className="px-2 py-1 text-xs bg-purple-200 dark:bg-purple-800 rounded"
                  >
                    {sort.direction === 'ASC' ? '↑ Asc' : '↓ Desc'}
                  </button>
                  <button 
                    onClick={() => removeSort(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <select
                onChange={(e) => e.target.value && addSort(e.target.value)}
                value=""
                className="w-full text-xs border border-purple-300 dark:border-purple-600 rounded px-2 py-1 bg-white dark:bg-surface-700"
              >
                <option value="">Add another sort...</option>
                {columns.filter(c => !sortBy.find(s => s.column === c.name)).map(col => (
                  <option key={col.name} value={col.name}>{col.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {/* Limit */}
        <div className="bg-surface-100 dark:bg-surface-800 rounded-xl p-4 border border-surface-200 dark:border-surface-700">
          <h4 className="text-sm font-semibold text-surface-700 dark:text-surface-300 mb-2">
            📏 Limit Results
          </h4>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={limit}
              onChange={(e) => {
                markAsModified();
                setLimit(parseInt(e.target.value) || 0);
              }}
              min="0"
              max="10000"
              className="w-24 text-sm border border-surface-300 dark:border-surface-600 rounded px-2 py-1 bg-white dark:bg-surface-700"
            />
            <span className="text-xs text-surface-500">rows</span>
          </div>
          <p className="text-xs text-surface-500 mt-1">
            Set to 0 for no limit (use with caution)
          </p>
        </div>
      </div>
      
      {/* Quick Tips */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-800">
        <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 mb-2">
          💡 Quick Tips
        </h4>
        <ul className="text-xs text-indigo-600 dark:text-indigo-400 space-y-1">
          <li>• <strong>Group By</strong>: Use for categorical data (names, types, dates)</li>
          <li>• <strong>Measures</strong>: Use aggregations like COUNT, SUM, AVG for numeric analysis</li>
          <li>• For a simple bar chart: Add one dimension and one measure</li>
          <li>• For pie charts: Use one dimension with COUNT or SUM</li>
        </ul>
      </div>
    </div>
  );
}

